import api from "api/flexapi";
import moment from "moment";
import accounting from "accounting";
import { setLoginMode } from "actions/loginActions";
import * as messaging from "../actions/messagingActions";
import * as R from "ramda";
import { AUTH_TYPE_AUTHSERVER } from "./loginActions";

export const NAVIGATION_MENU_REQUEST = "NAVIGATION_MENU_REQUEST";
export const NAVIGATION_MENU_SUCCESS = "NAVIGATION_MENU_SUCCESS";
export const NAVIGATION_MENU_FAILURE = "NAVIGATION_MENU_FAILURE";

export const LOAD_NEW_NAV_MENU_REQUEST = "LOAD_NEW_NAV_MENU_REQUEST";
export const LOAD_NEW_NAV_MENU_SUCCESS = "LOAD_NEW_NAV_MENU_SUCCESS";
export const LOAD_NEW_NAV_MENU_FAILURE = "LOAD_NEW_NAV_MENU_FAILURE";

export const CLIENT_SETTINGS_REQUEST = "CLIENT_SETTINGS_REQUEST";
export const CLIENT_SETTINGS_SUCCESS = "CLIENT_SETTINGS_SUCCESS";
export const CLIENT_SETTINGS_FAILURE = "CLIENT_SETTINGS_FAILURE";

export const LOAD_CUSTOM_CSS_REQUEST = "LOAD_CUSTOM_CSS_REQUEST";
export const LOAD_CUSTOM_CSS_SUCCESS = "LOAD_CUSTOM_CSS_SUCCESS";
export const LOAD_CUSTOM_CSS_FAILURE = "LOAD_CUSTOM_CSS_FAILURE";

const DEFAULT_DATE_FORMAT = "ll";

export const loadNavigationMenu = () => dispatch => {
  dispatch({ type: NAVIGATION_MENU_REQUEST });

  Promise.all([api.getNavigationMenu()])
    .then(responses => {
      const [response] = responses;

      const isMessagingEnabled =
        response.body &&
        response.body.messagingMenu &&
        response.body.messagingMenu.isMessagingEnabled;

      if (isMessagingEnabled) {
        messaging.getUnreadMessageCount()(dispatch);
      }

      dispatch({
        type: NAVIGATION_MENU_SUCCESS,
        payload: Object.assign({}, { ...response.body }),
      });
    })
    .catch(err =>
      dispatch({
        type: NAVIGATION_MENU_FAILURE,
        payload: err.response ? err.response.body : err,
      })
    );
};

export const loadNewNavMenus = () => dispatch => {
  dispatch({ type: LOAD_NEW_NAV_MENU_REQUEST });

  api
    .getNewNavMenus()
    .then(response => {
      const isMessagingEnabled =
        response.body && response.body.messagingMenu && response.body.messagingMenu.isMessagingEnabled;

      if (isMessagingEnabled) {
        messaging.getUnreadMessageCount()(dispatch);
      }

      dispatch({
        type: LOAD_NEW_NAV_MENU_SUCCESS,
        payload: { ...response.body },
      });
    })
    .catch(err =>
      dispatch({
        type: LOAD_NEW_NAV_MENU_FAILURE,
        payload: err.response ? err.response.body : err,
      })
    );
};
/**
 * Get something like "#,##0.00;-#,##00.00;0.00" similar to
 * https://support.office.com/en-us/article/Number-format-codes-5026bbd6-04bc-48cd-bf33-80f18b4eae68
 * and attempt to extract out chars for decimal and thousand, and number of
 * precision places
 *
 * @param  {[type]} formatString [description]
 * @return {Object} {
    decimal: ".",  // decimal point separator
    thousand: ",",  // thousands separator
    precision : 2   // decimal places
  }
 */
const extractNumberFormat = formatString => {
  //we get back something like "#,##0.00;-#,##00.00;0.00" similar to https://support.office.com/en-us/article/Number-format-codes-5026bbd6-04bc-48cd-bf33-80f18b4eae68
  //more currency formatting fun at http://www.thefinancials.com/?SubSectionID=curformat

  //positive | negative | zeros
  const formats = formatString.split(";");
  //first non-zero should be the decimal point separator
  const decimal = (formats[2].match(/[^0]/g) || [])[0];
  //split the zeros format string by the separator and we get number of trailing zeroes
  const precision =
    decimal !== undefined && (formats[2].split(decimal)[1] || { length: undefined }).length;
  //first char that is neither 0|#|<decimal separator> should be the thousands separator
  const thousand =
    [decimal, precision].every(x => x !== undefined) &&
    (formats[0].match(new RegExp(`[^0#${decimal}]`, "g")) || [])[0];

  return thousand
    ? {
        decimal,
        thousand,
        precision,
      }
    : console.error(
        "No formatting for currency and numbers from API. Falling back to defaults."
      ) || {
        decimal: ".",
        thousand: ",",
        precision: 2,
      };
};
const replaceAll = (find, replace) => formatString => {
  return formatString.replace(
    new RegExp(find.replace(/[-\\/\\^$*+?.()|[\]{}]/g, "\\$&"), "g"),
    replace
  );
};

const extractDateFormat = (formatString = "") => {
  return R.compose(replaceAll("y", "Y"), replaceAll("d", "D"))(formatString);
};

const findFormatById = (formatList, id) => {
  return formatList.find(x => x.id === id) || {};
};

const findFormatByList = R.compose(extractDateFormat, R.prop("value"), findFormatById);

export const tryDetectOAuth = authority => dispatch => {
  if (authority === AUTH_TYPE_AUTHSERVER) {
    dispatch(setLoginMode("oauth"));
  }
};

export const loadClientSettings = clientId => dispatch => {
  dispatch({ type: CLIENT_SETTINGS_REQUEST });
  api
    .getClientSettings(clientId)
    .then(response => {
      const clientSettings = { ...response.body};
      const {
        formatList,
        currencySymbol: symbol,
        backgroundImages,
        login: { options = [] } = {},
      } = clientSettings;

      const { authority } = options;

      //set date format from client-settings
      moment.defaultFormat = DEFAULT_DATE_FORMAT; //dateFormat;
      const llDateFormat = findFormatByList(formatList, "Date");

      const lllDateFormat = findFormatByList(formatList, "DateTime");

      moment.updateLocale(moment.locale(), {
        longDateFormat: {
          ll: llDateFormat ? llDateFormat : moment.localeData().longDateFormat("ll"),
          lll: lllDateFormat ? lllDateFormat : moment.localeData().longDateFormat("lll"),
        },
      });
      // figure out default format
      const numberFormat = extractNumberFormat(
        (formatList.find(x => x.id === "Decimal") || {}).value
      );
      accounting.setCurrencySettings({
        symbol,
        ...numberFormat,
      });
      accounting.setNumberSettings({
        ...numberFormat,
      });

      tryDetectOAuth(authority)(dispatch);

      dispatch({ type: CLIENT_SETTINGS_SUCCESS, payload: clientSettings });
    })
    .catch(err =>
      dispatch({
        type: CLIENT_SETTINGS_FAILURE,
        payload: err.response ? err.response.body : err,
      })
    );
};

export const redirectPage = path => (location.href = path);

export const loadCustomCss = clientName => dispatch => {
  dispatch({ type: LOAD_CUSTOM_CSS_REQUEST });
  api
    .getCustomCss(clientName)
    .then(response => {
      dispatch({ type: LOAD_CUSTOM_CSS_SUCCESS, payload: response.text });
    })
    .catch(err =>
      dispatch({
        type: LOAD_CUSTOM_CSS_FAILURE,
        payload: err.response ? err.response.body : err,
      })
    );
};



// WEBPACK FOOTER //
// ./actions/commonActions.jsx